import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axiosInstance';
import './FileAttachmentHandeling.css';

/**
 * FileAttachmentHandeling - Common component for displaying file attachments
 * Handles both server files and locally uploaded files
 * Shows image previews and handles file download/viewing
 *
 * @param {Object} file - File metadata object
 * @param {String} file.id - File ID (or local ID for uploaded files)
 * @param {String} file.name - File name
 * @param {String} file.mimeType - File MIME type
 * @param {Boolean} file.isLocalFile - Whether this is a locally uploaded file
 * @param {File} file.file - The actual File object (for local files)
 * @param {Boolean} allowDeletedFiles - Whether to allow deleted files
 */
const FileAttachmentHandeling = ({ file, allowDeletedFiles = false }) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const blobUrlRef = useRef(null);
  const errorToastShownRef = useRef(false);

  // Safety check
  if (!file || (!file.id && !file.file)) {
    return null;
  }

  const isImage = file.mimeType?.startsWith('image/');
  const isLocalFile = file.isLocalFile && file.file;
  const isStagedFile =
    Boolean(file.isStagedFile) ||
    (typeof file.id === 'string' && file.id.startsWith('staged-'));
  const stagedId =
    file.stagedId ||
    (typeof file.id === 'string' && file.id.startsWith('staged-')
      ? file.id.slice('staged-'.length)
      : null);
  // Staged files use staged-files endpoints; others use centralized files preview
  const downloadUrl =
    isStagedFile && stagedId
      ? // ? `/api/v1/staged-files/${stagedId}/download`
        `/api/v1/files/${stagedId}/download`
      : `/api/v1/files/${file.id}/preview`;

  // Load image preview
  useEffect(() => {
    if (!isImage) {
      setLoading(false);
      return;
    }

    if (imageError) {
      setLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        setLoading(true);

        let blobUrl;

        if (isLocalFile) {
          // For local files, create blob URL directly from File object
          blobUrl = URL.createObjectURL(file.file);
        } else {
          const url = allowDeletedFiles
            ? `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}includeDeleted=true`
            : downloadUrl;
          // For server or staged files, fetch from API
          const response = await axiosInstance.get(url, {
            responseType: 'blob',
            withCredentials: true,
            validateStatus: (status) => status < 500,
          });

          if (response.status !== 200) {
            if (!errorToastShownRef.current) {
              let errorMessage = t('fileAttachment.failedToOpenFile');
              try {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
              } catch {
                // Could not parse error
              }
              toast.error(errorMessage, { position: 'top-center' });
              errorToastShownRef.current = true;
            }
            setImageError(true);
            setLoading(false);
            return;
          }

          blobUrl = URL.createObjectURL(new Blob([response.data]));
        }

        blobUrlRef.current = blobUrl;
        setImageUrl(blobUrl);
        setLoading(false);
      } catch (error) {
        console.error('Error loading image:', error);
        if (!errorToastShownRef.current) {
          toast.error(t('fileAttachment.failedToOpenFileDetails'), {
            position: 'top-center',
          });
          errorToastShownRef.current = true;
        }
        setImageError(true);
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup blob URL on unmount or when file changes
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [file.id, file.file, isImage, isLocalFile, imageError, downloadUrl]);

  // Handle file click - opens in new tab
  const handleFileClick = async (e) => {
    e.preventDefault();

    try {
      let blobUrl;
      let mimeType = file.mimeType || 'application/octet-stream';

      if (isLocalFile) {
        // For local files, create blob URL from File object
        blobUrl = URL.createObjectURL(file.file);
      } else {
        const url = allowDeletedFiles
          ? `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}includeDeleted=true`
          : downloadUrl;
        // For server or staged files, fetch from API
        const response = await axiosInstance.get(url, {
          responseType: 'blob',
          withCredentials: true,
          validateStatus: (status) => status < 500,
        });

        if (response.status !== 200) {
          let errorMessage = t('fileAttachment.failedToOpenFile');
          try {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Could not parse error
          }
          toast.error(errorMessage, { position: 'top-center' });
          return;
        }

        mimeType =
          file.mimeType ||
          response.headers['content-type'] ||
          'application/octet-stream';
        blobUrl = URL.createObjectURL(
          new Blob([response.data], { type: mimeType })
        );
      }

      // Open in new tab
      window.open(blobUrl, '_blank');

      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error(t('fileAttachment.failedToOpenFileDetails'), {
        position: 'top-center',
      });
    }
  };

  // Render image preview
  if (isImage && !imageError && imageUrl) {
    return (
      <div className="file-attachment-item">
        <img
          src={imageUrl}
          alt={file.name}
          className="file-attachment-image"
          onClick={handleFileClick}
          title={t('fileAttachment.clickToOpen')}
        />
      </div>
    );
  }

  // Render loading state for images
  if (isImage && loading) {
    return (
      <div className="file-attachment-item">
        <div className="file-attachment-loading">
          {t('fileAttachment.loadingImage')}
        </div>
      </div>
    );
  }

  // Render error state for images (no UI, toast handled above)
  if (isImage && imageError) {
    return null;
  }

  // Render link for non-image files (PDFs, docs, etc.)
  return (
    <div className="file-attachment-item">
      <a
        href="#"
        onClick={handleFileClick}
        className="file-attachment-link"
        title={file.name}
      >
        {file.name}
      </a>
    </div>
  );
};

export default FileAttachmentHandeling;
